"use client";

import { useEffect, useRef, useState } from "react";

interface Story {
  id: number | string;
  heading: string;
  subheading: string;
  href: string;
  category: string;
  metrics: { value: string; label: string }[];
  image: string;
}

function Arrow({ size = 15 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M5 12h13M13 7l5 5-5 5" />
    </svg>
  );
}

// Filter row + heading + story grid. customer.css ki existing classes use karta hai.
export default function FeaturedStories() {
  const [stories, setStories] = useState<Story[] | null>(null);
  const [active, setActive] = useState("All");
  const [visible, setVisible] = useState(false);
  const gridRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;

    fetch("/api/case-studies/featured", { cache: "no-store" })
      .then((r) => r.json())
      .then((res) => {
        if (!cancelled) setStories(res.success ? res.data : []);
      })
      .catch(() => {
        if (!cancelled) setStories([]);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // same fade + upward drift reveal jaisa baaki page me hai
  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;

    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // filters data se hi bante hain (jo categories admin me daali hain)
  const filters = [
    "All",
    ...Array.from(new Set((stories ?? []).map((s) => s.category).filter(Boolean))),
  ];

  const shown =
    active === "All" ? stories ?? [] : (stories ?? []).filter((s) => s.category === active);

  return (
    <>
      {filters.length > 1 && (
        <div className="customerFilterRow">
          {filters.map((filter) => (
            <button
              key={filter}
              type="button"
              className={filter === active ? "customerSelected" : ""}
              onClick={() => setActive(filter)}
            >
              {filter}
            </button>
          ))}
        </div>
      )}

      <div className="customerSectionHeading">
        <h2>Featured Customer Stories</h2>
        <a href="/customers/Customer-Stories">
          View All Case Studies <Arrow />
        </a>
      </div>

      <div
        className={`customerStoryGrid customerReveal${visible ? " customerInView" : ""}`}
        ref={gridRef}
      >
        {stories === null ? (
          <p className="customerStoryEmpty">Loading customer stories...</p>
        ) : shown.length === 0 ? (
          <p className="customerStoryEmpty">Customer stories will appear here soon.</p>
        ) : (
          shown.map((story, i) => (
            <article
              className="customerStoryCard"
              key={story.id}
              style={{ transitionDelay: visible ? `${i * 0.08}s` : "0s" }}
            >
              <img src={story.image} alt={story.heading} loading="lazy" />

              <div className="customerStoryBody">
                {story.category && (
                  <span className="customerCategory">{story.category}</span>
                )}

                <h3>{story.heading}</h3>
                <p>{story.subheading}</p>

                {story.metrics.length > 0 && (
                  <div className="customerStoryMetrics">
                    {story.metrics.map((m) => (
                      <div key={`${m.value}-${m.label}`}>
                        <strong>{m.value}</strong>
                        <span>{m.label}</span>
                      </div>
                    ))}
                  </div>
                )}

                <a className="customerReadMore" href={story.href}>
                  Read the case study <Arrow />
                </a>
              </div>
            </article>
          ))
        )}
      </div>
    </>
  );
}
